import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { auth } from "@/lib/adminApi";
import { LayoutDashboard, Inbox, Image, BookOpen, FileText, Package, Quote, HelpCircle, Type, LogOut } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/inquiries", label: "Inquiries · CRM", icon: Inbox },
  { to: "/admin/media", label: "Media", icon: Image },
  { to: "/admin/content/case-studies", label: "Case Studies", icon: FileText },
  { to: "/admin/content/journal", label: "Journal", icon: BookOpen },
  { to: "/admin/content/event-types", label: "Event Types", icon: Type },
  { to: "/admin/content/packages", label: "Packages", icon: Package },
  { to: "/admin/content/testimonials", label: "Testimonials", icon: Quote },
  { to: "/admin/content/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/content/site-content", label: "Site Copy", icon: Type },
];

export function RequireAdmin({ children }) {
  const [state, setState] = useState("checking");
  useEffect(() => {
    if (!auth.isAuthed()) { setState("no"); return; }
    auth.me().then(() => setState("yes")).catch(() => { auth.logout(); setState("no"); });
  }, []);
  if (state === "checking") return <div className="flex h-screen items-center justify-center bg-aria-ink text-aria-ivory/60">Loading…</div>;
  if (state === "no") return <Navigate to="/admin/login" replace />;
  return children;
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const doLogout = () => { auth.logout(); navigate("/admin/login"); };

  return (
    <div className="flex min-h-screen bg-aria-sand" data-testid="admin-layout">
      <aside className="fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-aria-ink text-aria-ivory">
        <div className="px-7 py-8">
          <img src="/aria-logo-light.png" alt="ARIA" className="h-9 w-auto" />
          <div className="mt-3 text-[0.6rem] uppercase tracking-[0.28em] text-aria-brass">Studio</div>
        </div>
        <nav className="flex-1 overflow-y-auto px-4">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `mb-1 flex items-center gap-3 rounded px-3 py-2.5 text-sm transition-colors ${
                  isActive ? "bg-aria-ivory/10 text-aria-ivory" : "text-aria-ivory/55 hover:text-aria-ivory"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <button onClick={doLogout} data-testid="admin-logout" className="m-4 flex items-center gap-3 rounded px-3 py-2.5 text-sm text-aria-ivory/55 hover:text-aria-ivory">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </aside>
      <main className="ml-64 flex-1 p-8 md:p-12">
        <Outlet />
      </main>
    </div>
  );
}
